import React, { useRef, useEffect, useState, useMemo } from 'react';
import { RefinedRule, RuleDescriptor, RuleExpression, RulesDescriptor } from 'src/data/types/descriptor';
import Select, { SingleValueProps, components, OptionProps, MenuProps, MenuListProps } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { getServerSideData } from 'src/utils/server-side';
import { Segment, Tour } from 'src/types';
import { generalOperators, getPropIds, allGroupsSettings, PredefinedProp, useBasicDefinitionForProp, generalWideOperators, OperatorType } from './definitions';
import CollapsedRuleDescription from './CollapsedRuleDescription';
import { Checklist } from 'src/data/types/base';

export default function RulesDescriptorComponent(props: {
    rulesDescriptor: RulesDescriptor,
    onChanged: (rulesDescriptor: RulesDescriptor) => void,
    allTours?: Tour[],
    allSegments?: Segment[],
    allChecklists?: Checklist[],
    supportsEvents?: boolean,
    supportsUserInteractivity?: boolean,
    supportsChecklistStates?: boolean
    supportsSurveys?: boolean,
    supportsAnnouncements?: boolean
}) {
    const [mainRulesGroup, updateMainRulesGroup] = useState(props.rulesDescriptor || createDefaultDescriptor());
    const allTours = props.allTours || getServerSideData('tours');
    const allSegments = props.allSegments || getServerSideData('segments');
    const allChecklists = props.allChecklists || getServerSideData('checklists');
    const [changePropagator, propagateChange] = useState(0); // onChanged handling

    const addNewRule = () => {
        updateMainRulesGroup((state) => {
            const newState = { ...state };

            newState.rules = [...newState.rules, {
                expression: {
                    prop: 'user.username',
                    op: '=',
                    val: ''
                }
            }];

            return newState;
        });

        propagateChange(changePropagator + 1);
    }

    const updateExpression = (index: number, prop: string, op: string, val: string, data?: string) => {
        console.log('UPD EXT', prop, op, val)
        updateMainRulesGroup((state) => {
            const newState = { ...state };

            newState.rules = state.rules.map((rule, i) => {
                if (i == index) {
                    return { ...rule, expression: { prop, op, val, data } }
                } else {
                    return rule;
                }
            });

            return newState;
        });

        propagateChange(changePropagator + 1);
    }

    const updateCondOp = (condOp: string) => {
        updateMainRulesGroup((state) => {
            const newState = { ...state };

            newState.condOp = condOp;

            return newState;
        });

        propagateChange(changePropagator + 1);
    }

    const updateRefinedRule = (index: number, refined: RefinedRule) => {
        console.log('update ref', refined);
        updateMainRulesGroup((state) => {
            const newState = { ...state };

            newState.rules = state.rules.map((rule, i) => {
                if (i == index) {
                    return { ...rule, refinedRule: refined };
                } else {
                    return rule;
                }
            });

            return newState;
        });

        propagateChange(changePropagator + 1);
    }

    const removeExpression = (index: number) => {
        updateMainRulesGroup((state) => {
            const newState = { ...state };

            newState.rules = state.rules.map((rule, i) => {
                if (i == index) return null;
                return rule;
            }).filter(i => i != null);

            return newState;
        });

        propagateChange(changePropagator + 1);
    }

    useEffect(() => {
        if (changePropagator > 0) {
            props.onChanged(mainRulesGroup);
        }
    }, [changePropagator]);

    return <div>
        <div className="rules-container">
            <div className="rules-condition-selector">
                <select className="form-control" value={mainRulesGroup.condOp} onChange={e => updateCondOp(e.target.value)}>
                    <option value="AND">All of these (AND)</option>
                    <option value="OR">Any of these (OR)</option>
                </select>
            </div>

            {mainRulesGroup.rules.map((rule, index) =>
                <RuleRow
                    key={index}
                    rule={rule}
                    index={index}
                    allTours={allTours}
                    allSegments={allSegments}
                    allChecklists={allChecklists}
                    mainRulesGroup={mainRulesGroup}
                    supportsEvents={props.supportsEvents}
                    supportsUserInteractivity={props.supportsUserInteractivity}
                    supportsChecklistStates={props.supportsChecklistStates}
                    supportsSurveys={props.supportsSurveys}
                    supportsAnnouncements={props.supportsAnnouncements}
                    onExpressionChanged={(i, prop, op, val, data) => updateExpression(i, prop, op, val, data)}
                    onExpressionRemoved={(i) => removeExpression(i)}
                    onRefinedRuleChanged={(i, r) => updateRefinedRule(i, r)} />
            )}

            <div className="rule-row">
                <div className="rule-cond">
                    {mainRulesGroup.rules.length > 0 ? mainRulesGroup.condOp : 'If'}
                </div>
                <div>
                    <a href="#" onClick={(e) => { e.preventDefault(); addNewRule(); }} className="mt-2 ml-0 d-block"><i className="fas fa-plus-circle"></i> Add condition</a>
                </div>
            </div>
        </div>
    </div>
}

function RuleRow(props: {
    rule: RuleDescriptor,
    index: number,
    allTours: Tour[],
    allSegments: Segment[],
    allChecklists: Checklist[],
    supportsEvents?: boolean,
    supportsUserInteractivity?: boolean,
    supportsChecklistStates?: boolean,
    supportsSurveys?: boolean,
    supportsAnnouncements?: boolean
    mainRulesGroup: RulesDescriptor,
    onExpressionChanged: (index: number, prop: string, op: string, val: string, data?: string) => void,
    onExpressionRemoved: (index: number) => void,
    onRefinedRuleChanged: (index: number, refined: RefinedRule) => void
}) {
    const expression = props.rule.expression;
    const [isExpanded, setIsExpanded] = useState(false);

    const [selectedGroupId, selectedAttrId] = getPropIds(expression.prop);

    const [groupedPredefinedProps, attrsOfGroup, attrConfig] = useBasicDefinitionForProp(
        expression.prop,
        props.allTours,
        props.allSegments,
        props.allChecklists,
        false,
        {
            supportsEvents: props.supportsEvents,
            supportsUserInteractivity: props.supportsUserInteractivity,
            supportsChecklistStates: props.supportsChecklistStates,
            supportsAnnoucements: props.supportsAnnouncements,
            supportsSurveys: props.supportsSurveys
        });

    const allGroups = groupedPredefinedProps.map(i => ({ groupId: i.groupId, label: i.label }));

    let probablyWrontName = false;

    if (selectedAttrId && selectedAttrId.startsWith('props.')) {
        const cleanAttrId = selectedAttrId.replace('props.', '');
        attrsOfGroup.push({
            attrId: selectedAttrId,
            text: cleanAttrId,
            value: selectedAttrId
        });

        if (selectedAttrId.startsWith('props.props.')) {
            probablyWrontName = true;
        }
    }

    const groupSelected = (groupId) => {
        const newGroupAttrs = groupedPredefinedProps.filter(i => i.groupId == groupId)[0]?.attributes;
        let ident = '';
        if (newGroupAttrs.length == 1) {
            const attrId = newGroupAttrs[0].attrId;
            if (attrId) {
                ident = `${groupId}.${attrId}`;
            } else {
                ident = groupId;
            }
        } else {
            ident = groupId;
        }
        const operators = newGroupAttrs[0]?.operators || generalOperators;

        let finalOp = operators[0].value;
        props.onExpressionChanged(props.index, ident, finalOp, null);
        props.onRefinedRuleChanged(props.index, null);
    }
    const attrSelected = (attrId) => {
        const finalIdent = `${selectedGroupId}.${attrId}`;
        let finalOp = expression.op;
        const attrDef = attrsOfGroup.filter(i => i.value == finalIdent)[0];
        const operators = attrDef?.operators || attrsOfGroup[0]?.operators || generalOperators;

        if (!selectedAttrId && attrId) {
            finalOp = operators[0].value;
        }

        if (!operators.some(i => i.value == finalOp)) {
            finalOp = operators[0].value;
        }

        props.onExpressionChanged(props.index, finalIdent, finalOp, expression.val, expression.data);
    }

    const attrCreated = (attrId) => {
        attrSelected('props.' + attrId)
    }

    const hasCustomAttrComponent = !!attrConfig?.customAttrComponent;
    const PropertyCustomAttrComponent = attrConfig?.customAttrComponent;

    const hasCustomRefineComponent = !!attrConfig?.customRefineComponent;
    const PropertyCustomRefineComponent = attrConfig?.customRefineComponent;

    const hasCustomSettingsComponent = !!attrConfig?.customSettingsComponent;
    const PropertyCustomSettingsComponent = attrConfig?.customSettingsComponent;

    const getRuleOperators = () => {
        if (attrConfig && attrConfig.operators) {
            return attrConfig.operators;
        }

        if (selectedGroupId == 'user') {
            return generalWideOperators;
        }

        return generalOperators;
    }

    const hasRuleOperatorValue = () => {
        const op = getRuleOperators().filter(i => i.value == expression.op)[0];
        if (op) {
            return op.hasValue !== false;
        }
        return true;
    }

    const getRuleValType = () => {
        if (!hasRuleOperatorValue()) return 'no-value';

        if (attrConfig && attrConfig.opts) {
            return 'options';
        } else if (attrConfig && attrConfig.hasValue === false) {
            return 'no-value';
        } else {
            return 'text';
        }
    }

    const getRuleOptions = () => {
        if (!attrConfig) return [];

        return attrConfig.opts;
    }

    const getRuleInputPostfix = () => {
        if (attrConfig && attrConfig.operators) {
            const op = attrConfig.operators.filter(o => o.value == expression.op)[0];

            return op ? op.inputPostfix : '';
        }

        if (selectedGroupId == 'user') {
            const op = generalWideOperators.filter(o => o.value == expression.op)[0];

            return op ? op.inputPostfix : '';
        }

        return '';
    }

    useEffect(() => {
        const closeOnClick = (e: MouseEvent) => {
            setIsExpanded(false);
        }

        window.addEventListener('click', closeOnClick);

        return () => {
            window.removeEventListener('click', closeOnClick);
        }
    }, []);

    const groupSettings = allGroupsSettings[selectedGroupId] || {};

    const AttributeSelectComponent = groupSettings.allowCreating ? CreatableSelect : Select;

    return <div className="rule-row">
        <div className="rule-cond">
            {props.index > 0 ? props.mainRulesGroup.condOp : 'If'}
        </div>
        <div className="rule-summary" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsExpanded(true); }}>
            <div className="rule-desc">
                <CollapsedRuleDescription rule={props.rule} allTours={props.allTours} allSegments={props.allSegments} allChecklists={props.allChecklists}
                    supportsEvents={props.supportsEvents} supportsUserInteractivity={props.supportsUserInteractivity}
                    supportsChecklists={props.supportsChecklistStates}
                    supportsSurveys={props.supportsSurveys} />
            </div>
            <div className="rule-remove" onClick={(e) => { e.preventDefault(); e.stopPropagation(); props.onExpressionRemoved(props.index); }}><i className="fas fa-trash"></i></div>

            <div className="rule-editor-dropdown" style={{ display: isExpanded ? 'block' : 'none' }}>
                <div className="rule-editor">
                    <div className='d-flex'>
                        <div className=''>
                            <Select
                                className='cond-prop-selector'
                                classNamePrefix='transparent-select'
                                menuPlacement='top'
                                isSearchable={false}
                                components={{ Option: RuleGroupOption, SingleValue: RuleGroupSelectedOption }}
                                options={allGroups.map(i => ({ groupId: i.groupId, label: i.label }))}
                                value={allGroups.filter(i => i.groupId == selectedGroupId)[0]}
                                onChange={(e) => groupSelected(e.groupId)}
                                getOptionValue={i => i.groupId}
                            />
                        </div>
                        <div className='flex-grow-1 ml-1 hide-if-empty'>
                            {hasCustomAttrComponent && <PropertyCustomAttrComponent expression={expression} onAttrSelected={attrSelected} />}

                            {!hasCustomAttrComponent && attrsOfGroup.length > 1 && <AttributeSelectComponent
                                className='cond-prop-selector'
                                classNamePrefix='rselect'
                                menuPlacement='top'
                                options={attrsOfGroup.sort((a, b) => a.text?.localeCompare(b.text))}
                                getOptionLabel={i => i.text}
                                getOptionValue={i => i.attrId}
                                value={attrsOfGroup.filter(i => i.attrId == selectedAttrId)[0] || null}
                                onChange={(e: PredefinedProp) => attrSelected(e.attrId)}
                                placeholder={groupSettings.allowCreating ? 'Select or enter custom...' : 'Select...'}
                                onCreateOption={(a) => attrCreated(a)}
                                /** @ts-ignore */
                                getNewOptionData={(inputValue) => ({ attrId: inputValue, text: `Use "${inputValue}"` })}
                                formatCreateLabel={(input) => `Use "${input}"`}
                                components={{
                                    SingleValue: SingleValueEx,
                                    MenuList: groupSettings.allowCreating ? MenuCreatableListEx : components.MenuList
                                }}
                            />}

                            {probablyWrontName && <div className='d-flex align-items-center text-sm py-1'>
                                <i className="fas fa-exclamation-triangle"></i>
                                <span className='ml-2'>Custom property names shouldn't start with 'props.'.</span></div>}
                        </div>
                    </div>

                    {hasCustomSettingsComponent && selectedAttrId &&
                        <PropertyCustomSettingsComponent
                            expression={expression}
                            onChange={(dataStr) => props.onExpressionChanged(props.index, expression.prop, expression.op, expression.val, dataStr)}
                            components={{
                                StandardOperatorsSelector: <OperatorsSelector expression={expression} getRuleOperators={getRuleOperators} onChange={(val) =>
                                    props.onExpressionChanged(props.index, expression.prop, val, expression.val, expression.data)} />,
                                TextValueInput: <input type="text" className="form-control" value={expression.val || ''}
                                    onChange={(e) => props.onExpressionChanged(props.index, expression.prop, expression.op, e.target.value, expression.data)} />
                            }} />}

                    {!hasCustomSettingsComponent && selectedAttrId && <>
                        <div className="cond-operator">
                            {/* <Select
                                classNamePrefix='rselect'
                                menuPlacement='top'
                                options={getRuleOperators()}
                                getOptionLabel={i => i.text}
                                value={getRuleOperators().filter(o => o.value == expression.op)[0] || getRuleOperators()[0]}
                                onChange={(e) => props.onExpressionChanged(props.index, expression.prop, e.value, expression.val)} /> */}

                            <OperatorsSelector expression={expression} getRuleOperators={getRuleOperators} onChange={(val) =>
                                props.onExpressionChanged(props.index, expression.prop, val, expression.val, expression.data)} />
                        </div>

                        <div className="cond-value">
                            {getRuleValType() == 'text' &&
                                <input type="text" className="form-control" value={expression.val || ''}
                                    onChange={(e) => props.onExpressionChanged(props.index, expression.prop, expression.op, e.target.value, expression.data)} />
                            }
                            {getRuleInputPostfix() && getRuleValType() == 'text' &&
                                <span className="ml-1">{getRuleInputPostfix()}</span>
                            }
                            {getRuleValType() == 'options' &&
                                <Select
                                    classNamePrefix='rselect'
                                    menuPlacement='top'
                                    options={getRuleOptions()}
                                    getOptionLabel={i => i.text}
                                    value={getRuleOptions().filter(i => i.value == expression.val)[0] || null}
                                    onChange={(e) => props.onExpressionChanged(props.index, expression.prop, expression.op, e.value, expression.data)}
                                />
                            }
                        </div>

                        {hasCustomRefineComponent &&
                            <PropertyCustomRefineComponent
                                rule={props.rule}
                                onRefinedRuleChanged={(r: RefinedRule) => props.onRefinedRuleChanged(props.index, r)}
                                onRuleExpressionChanged={(r: RuleExpression) => props.onExpressionChanged(props.index, r?.prop, r?.op, r?.val, r?.data)}
                                onAddRefine={() => props.onRefinedRuleChanged(props.index, { atLeastTimes: 1 })}
                                onRemoveRefine={() => props.onRefinedRuleChanged(props.index, null)} />}
                    </>
                    }
                </div>
            </div>
        </div>
    </div>
}

function OperatorsSelector(props: { getRuleOperators: () => OperatorType[], expression: RuleExpression, onChange: (operator: string) => void }) {
    const getRuleOperators = props.getRuleOperators;
    const expression = props.expression;

    return <Select
        classNamePrefix='rselect'
        menuPlacement='top'
        options={getRuleOperators()}
        getOptionLabel={i => i.text}
        value={getRuleOperators().filter(o => o.value == expression.op)[0] || getRuleOperators()[0]}
        onChange={(e) => props.onChange(e.value)} />
}

function MenuCreatableListEx(props: MenuListProps<PredefinedProp>) {
    return <components.MenuList {...props}>
        {props.children}

        <div className='rselect-creatable-note'>Or enter a custom property name</div>
    </components.MenuList>
}

function SingleValueEx(props: SingleValueProps<PredefinedProp>) {
    return <components.SingleValue {...props}>
        {!props.selectProps.menuIsOpen && <div>{props.data?.text}</div>}
        {props.selectProps.menuIsOpen && <div style={{ color: 'hsl(0, 0%, 50%)' }}>Select or enter custom name</div>}
    </components.SingleValue>
}

function GroupIcon(props: { type: string }) {
    switch (props.type) {
        case 'user': return <span className='transparent-select__fix-icon'><i className='fas fa-users'></i></span>
        case 'env': return <span className='transparent-select__fix-icon'><i className='fas fa-globe-americas'></i></span>
        case 'device': return <span className='transparent-select__fix-icon'><i className='fas fa-laptop'></i></span>
        case 'tour': return <span className='transparent-select__fix-icon'><i className='fas fa-directions'></i></span>
        case 'segment': return <span className='transparent-select__fix-icon'><i className='fas fa-boxes'></i></span>
        case 'event': return <span className='transparent-select__fix-icon'><i className='fas fa-bolt'></i></span>
        case 'uiact': return <span className='transparent-select__fix-icon'><i className='fas fa-mouse-pointer'></i></span>
        case 'chklst': return <span className='transparent-select__fix-icon'><i className='fas fa-tasks'></i></span>
        case 'surv': return <span className='transparent-select__fix-icon'><i className='fas fa-poll-h'></i></span>
    }

    return null;
}

function RuleGroupSelectedOption(props: SingleValueProps) {
    return <components.SingleValue {...props}>
        {/** @ts-ignore */}
        <GroupIcon type={props.data?.groupId} />{props.data?.label}
    </components.SingleValue>
}

function RuleGroupOption(props: OptionProps) {
    return <components.Option {...props}>
        {/** @ts-ignore */}
        <GroupIcon type={props.value} />{props.label}
    </components.Option>
}

function createDefaultDescriptor(): RulesDescriptor {
    return {
        condOp: 'and',
        rules: []
    }
}
